import { useLingui } from "@lingui/react/macro";
import { Clock } from "lucide-react";
import { ProgressRing } from "@/components/ui/progress-ring";
import { TimeChip } from "@/components/ui/time-chip";
import { elapsedPct, neverStarted } from "@/lib/elapsed";
import type { CookingSession } from "@/types/cooking-session";

/**
 * What a cook's progress looks like on the working board (T-1101).
 *
 * Both take seconds, and both take them from the caller: one row, one clock,
 * so the ring and the pill can never disagree about the same cook.
 */

export function ProgressCircle({
  elapsedSeconds,
  target,
  large,
}: {
  elapsedSeconds: number;
  target: number;
  large?: boolean;
}) {
  // From seconds: against a 27-minute target, whole minutes moved the ring
  // 3.7% at a time and held still in between.
  const pct = elapsedPct(elapsedSeconds, target);
  const isLate = elapsedSeconds > target * 60;
  return <ProgressRing pct={pct} size={large ? "lg" : "sm"} tone={isLate ? "late" : "ink"} />;
}

export function TimerPill({
  session,
  elapsedSeconds,
}: {
  session: CookingSession;
  elapsedSeconds: number;
}) {
  const { t } = useLingui();
  const icon = <Clock className="h-4.5 w-4.5 shrink-0 text-(--color-text-secondary)" />;

  // Completed straight off the deck — the orders board's Complete button
  // dismisses tickets nobody picked up. Reporting those as "0:00 / 27m" claims
  // a cook that never ran.
  if (neverStarted(session)) {
    return <TimeChip icon={icon}>{t`Not started`}</TimeChip>;
  }

  return <TimeChip icon={icon} elapsedSeconds={elapsedSeconds} target={session.targetMinutes} />;
}
